import { useEffect, useCallback } from "react";
import capitalize from "lodash/capitalize";
import map from "lodash/map";
import size from "lodash/size";
import { Link, useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Translator } from "@/components/i18n";

import { DeleteThreadButton } from "./DeleteThreadButton";
import { EditThreadButton } from "./EditThreadButton";
import { useChatSession } from "@/client/useChatSession";
import { useChatInteract } from "@/client/useChatInteract";
// import { useChatMessages } from "@/client/useChatMessages";
import { ThreadHistory } from "@/client/types";
import { useRecoilState, useRecoilValue } from "recoil";
import { companyState, currentThreadIdState } from "@/client/state";

interface Props {
  threadHistory?: ThreadHistory;
  error?: string;
  fetchThreads: () => void;
  isFetching: boolean;
  isLoadingMore: boolean;
}

const ThreadList = ({
  threadHistory,
  error,
  fetchThreads,
  isFetching,
  isLoadingMore,
}: Props) => {
  const { idToResume } = useChatSession();
  const { clear } = useChatInteract();
  const currentThreadId = useRecoilValue(currentThreadIdState);

  const refetchThreads = useCallback(fetchThreads, []);

  const navigate = useNavigate();

  const [threadCompany, setThreadCompany] = useRecoilState(companyState);

  // Fetch threads when currentThreadId changes
  useEffect(() => {
    if (currentThreadId) {
      refetchThreads();
    }
  }, [currentThreadId, refetchThreads]);

  useEffect(() => {
    if (!threadHistory?.timeGroupedThreads || !currentThreadId) return;

    for (const group of Object.values(threadHistory.timeGroupedThreads)) {
      const matchedThread = group.find(
        (thread) => thread.thread_id === currentThreadId
      );
      if (
        matchedThread &&
        String(matchedThread.company_id) !== String(threadCompany)
      ) {
        // console.log("Matched Thread Company: ", matchedThread.company_id);
        // console.log("Thread Company: ", threadCompany);
        setThreadCompany(String(matchedThread.company_id));
        break;
      }
    }
  }, [threadHistory, currentThreadId]);

  if (isFetching || (!threadHistory?.timeGroupedThreads && isLoadingMore)) {
    return (
      <>
        {[1, 2, 3].map((index) => (
          <Box key={`threads-skeleton-${index}`} sx={{ mt: 2 }}>
            <Skeleton width={100} />
            {[1, 2].map((childIndex) => (
              <Stack
                key={`threads-skeleton-${index}-${childIndex}`}
                sx={{
                  py: 2,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Skeleton width={30} />
                <Skeleton width={"100%"} />
              </Stack>
            ))}
          </Box>
        ))}
      </>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!threadHistory) {
    return null;
  }

  if (size(threadHistory?.timeGroupedThreads) === 0) {
    return (
      <Alert variant="standard" severity="info">
        <Translator path="components.organisms.threadHistory.sidebar.ThreadList.empty" />
      </Alert>
    );
  }

  const handleDeleteThread = (threadId: string) => {
    if (threadId === idToResume || threadId === currentThreadId) {
      clear();
    }
    if (threadId === currentThreadId) {
      navigate("/");
    }
    fetchThreads();
  };

  return (
    <List
      sx={{
        height: 0,
        "& ul": { padding: 0 },
      }}
      subheader={<li />}
    >
      {map(threadHistory.timeGroupedThreads, (items, index) => {
        return (
          <li key={`section-${index}`}>
            <ul>
              <ListSubheader
                sx={{
                  px: 0,
                  mt: 2,
                  backgroundColor: "transparent",
                }}
              >
                <Typography
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    color: "text.disabled",
                    fontSize: "12px",
                  }}
                >
                  {(() => {
                    if (index === "Today") {
                      return (
                        <Translator path="components.organisms.threadHistory.sidebar.ThreadList.today" />
                      );
                    } else if (index === "Yesterday") {
                      return (
                        <Translator path="components.organisms.threadHistory.sidebar.ThreadList.yesterday" />
                      );
                    } else if (index === "Previous 7 days") {
                      return (
                        <Translator path="components.organisms.threadHistory.sidebar.ThreadList.previous7days" />
                      );
                    } else if (index === "Previous 30 days") {
                      return (
                        <Translator path="components.organisms.threadHistory.sidebar.ThreadList.previous30days" />
                      );
                    } else {
                      return <>{index}</>;
                    }
                  })()}
                </Typography>
              </ListSubheader>
              {map(items, (thread) => {
                const isResumed =
                  idToResume === thread.thread_id && !currentThreadId;

                const isSelected =
                  isResumed || currentThreadId === thread.thread_id;

                return (
                  <Stack
                    component={Link}
                    key={`thread-${thread.thread_id}`}
                    id={`thread-${thread.thread_id}`}
                    sx={(theme) => ({
                      textDecoration: "none",
                      cursor: "pointer",
                      p: 1.5,
                      // mb: 0.5,
                      // gap: 0.5,
                      flexDirection: "row",
                      justifyContent: "space-between",
                      borderRadius: 1,
                      backgroundColor: isSelected
                        ? theme.palette.mode === "dark"
                          ? "background.default"
                          : "primary.dark"
                        : "transparent",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "background.default"
                            : isSelected
                            ? "primary.dark"
                            : "primary.light",
                      },
                    })}
                    to={isResumed ? "" : `/thread/${thread.thread_id}`}
                  >
                    <Stack
                      direction="row"
                      width="100%"
                      justifyContent="space-between"
                    >
                      <Stack
                        sx={{
                          alignItems: "center",
                          flexDirection: "row",
                          gap: 1.5,
                          overflow: "hidden",
                          color: (theme) => theme.palette.text.primary,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontWeight: 500,
                            fontSize: "13px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: isSelected
                              ? theme.palette.mode === "dark"
                                ? "primary.main"
                                : "secondary.contrastText"
                              : "text.primary",
                          })}
                        >
                          {capitalize(thread.thread_name || "Unknown")}
                        </Typography>
                      </Stack>
                      {isSelected ? (
                        <Stack
                          direction="row"
                          justifyContent="end"
                          sx={(theme) => ({
                            color: isSelected
                              ? theme.palette.mode === "dark"
                                ? "primary.main"
                                : "secondary.contrastText"
                              : "text.primary",
                          })}
                        >
                          <EditThreadButton
                            threadId={thread.thread_id}
                            initialThreadName={thread.thread_name}
                            fetchThreads={fetchThreads}
                          />
                          <DeleteThreadButton
                            threadId={thread.thread_id}
                            onDelete={() =>
                              handleDeleteThread(thread.thread_id)
                            }
                          />
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                );
              })}
            </ul>
          </li>
        );
      })}
      <Stack alignItems={"center"}>
        <CircularProgress
          id={"chat-history-loader"}
          size={30}
          sx={{ my: 1, opacity: isLoadingMore ? 1 : 0 }}
        />
      </Stack>
    </List>
  );
};

export { ThreadList };
